"use client";

import { GrowthBook, GrowthBookProvider } from "@growthbook/growthbook-react";
import { logger, submitEvent } from "@platform/browser-logger";
import { type ReactNode, useEffect, useRef, useState } from "react";

import type { FeatureFlagAttributes } from "../lib/attributes";

export interface GBClientProviderProps {
  readonly apiHost?: string;
  /** Without a client key the provider never contacts GrowthBook; only forced flags are on. */
  readonly clientKey?: string;
  /** Flag keys to force on, for local development. */
  readonly forcedFlags?: readonly string[];
  /** The signed-in user, or undefined when nobody is signed in. Kept in sync as it changes. */
  readonly attributes?: FeatureFlagAttributes;
  readonly children: ReactNode;
}

interface ClientFlagsSetup {
  readonly growthbook: GrowthBook;
  readonly clientKey?: string;
  readonly forcedFlags: readonly string[];
}

const NO_FORCED_FLAGS: readonly string[] = [];

const applyForcedFlags = ({
  growthbook,
  forcedFlags,
}: ClientFlagsSetup): void => {
  if (forcedFlags.length === 0) return;
  growthbook.setForcedFeatures(
    new Map(forcedFlags.map((flag) => [flag, true])),
  );
};

const createSetup = ({
  apiHost,
  clientKey,
  forcedFlags = NO_FORCED_FLAGS,
  attributes,
}: Omit<GBClientProviderProps, "children">): ClientFlagsSetup => {
  const growthbook = new GrowthBook({
    apiHost,
    clientKey,
    attributes,
    enableDevMode: true,
    trackingCallback: (experiment, result) => {
      submitEvent("experiment_viewed", {
        experiment_id: experiment.key,
        variation_id: result.variationId,
        value: result.value,
      });
    },
  });
  const setup = { growthbook, clientKey, forcedFlags };
  // Applied before the first render so routes gated on a forced flag resolve
  // without waiting for the remote payload.
  applyForcedFlags(setup);
  return setup;
};

const loadRemoteFlags = async (setup: ClientFlagsSetup): Promise<void> => {
  const { success, source, error } = await setup.growthbook.init({
    streaming: true,
  });
  if (!success) {
    logger.warn("GrowthBook failed to load flags, using defaults", {
      source,
      error: error?.message,
    });
  }
  // The remote payload replaces forced values, so they are applied again.
  applyForcedFlags(setup);
};

/**
 * Provides GrowthBook to a client-rendered tree, in a Vite app or a Nextjs app.
 * Every prop except `attributes` is read once, on mount. Each mount gets its own
 * instance rather than a module-level one: in Nextjs, client components also render on
 * the server, where a module-level instance would be shared by every request.
 */
export const GBClientProvider = ({
  children,
  ...config
}: GBClientProviderProps): ReactNode => {
  const [setup] = useState(() => createSetup(config));
  const pendingDestroy = useRef<ReturnType<typeof setTimeout>>(undefined);
  const userId = config.attributes?.userId;
  const role = config.attributes?.role;
  const email = config.attributes?.email;
  const brokerId = config.attributes?.brokerId;

  useEffect(() => {
    if (!setup.clientKey) return;
    void loadRemoteFlags(setup);
  }, [setup]);

  // GrowthBook keeps every streaming instance subscribed until it is destroyed. In
  // development, StrictMode runs this cleanup and the effect again immediately, and the
  // instance from useState can't be recreated, so the destroy waits a tick for that
  // replay to cancel it. Only a real unmount gets through.
  useEffect(() => {
    clearTimeout(pendingDestroy.current);
    return () => {
      pendingDestroy.current = setTimeout(() => setup.growthbook.destroy());
    };
  }, [setup]);

  // Keyed on the values rather than the object, since callers build a new
  // attributes object on every render.
  useEffect(() => {
    const attributes =
      userId === undefined
        ? {}
        : { userId, role, email, ...(brokerId && { brokerId }) };
    void setup.growthbook.setAttributes(attributes);
  }, [setup, userId, role, email, brokerId]);

  return (
    <GrowthBookProvider growthbook={setup.growthbook}>
      {children}
    </GrowthBookProvider>
  );
};
